import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faCheck,
  faEye,
  faEyeSlash,
  faLock,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/QuickAccessModal.css';
import GoogleSignInButton from '../auth/GoogleSignInButton';

const QuickAccessModal = ({
  open,
  courseTitle,
  email,
  password,
  confirmPassword,
  error,
  loading,
  requiresAccount = false,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onGoogleCredential,
  onGoogleError,
  onSubmit,
  onDismiss,
}) => {
  const emailRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (open) emailRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !loading) onDismiss();
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [loading, onDismiss, open]);

  if (!open) return null;

  return (
    <div className="quick-access-backdrop" role="presentation">
      <section
        className="quick-access-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-access-title"
      >
        <button
          type="button"
          className="quick-access-close"
          onClick={onDismiss}
          disabled={loading}
          aria-label="Fechar"
          title="Fechar"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <div className="quick-access-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faBookOpen} />
        </div>
        <p className="quick-access-eyebrow">Seu progresso já começou</p>
        <h2 id="quick-access-title">Salve esta aula e continue quando quiser</h2>
        <p className="quick-access-copy">
          Crie seu acesso gratuito para não perder o que já estudou em <strong>{courseTitle}</strong>.
          Assim você volta exatamente de onde parou, conclui o curso e faz a prova no seu tempo.
        </p>

        <ul className="quick-access-benefits">
          <li><FontAwesomeIcon icon={faCheck} /> Cadastro rápido com seus dados confirmados</li>
          <li><FontAwesomeIcon icon={faCheck} /> Matrícula e progresso salvos automaticamente</li>
        </ul>

        <GoogleSignInButton
          onCredential={onGoogleCredential}
          onError={onGoogleError}
          disabled={loading}
          dividerLabel="ou use e-mail e senha"
        />

        <form className="quick-access-form" onSubmit={onSubmit} noValidate>
          <label htmlFor="quick-access-email">E-mail</label>
          <input
            ref={emailRef}
            id="quick-access-email"
            type="email"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            autoComplete="email"
            placeholder="voce@exemplo.com"
            disabled={loading}
            required
          />

          <label htmlFor="quick-access-password">Senha</label>
          <div className="quick-access-password">
            <FontAwesomeIcon icon={faLock} aria-hidden="true" />
            <input
              id="quick-access-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              autoComplete="current-password"
              placeholder="Mínimo de 6 caracteres"
              minLength="6"
              maxLength="72"
              disabled={loading}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              disabled={loading}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>

          <label htmlFor="quick-access-confirm-password">Confirmar senha</label>
          <div className="quick-access-password">
            <FontAwesomeIcon icon={faLock} aria-hidden="true" />
            <input
              id="quick-access-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(event) => onConfirmPasswordChange(event.target.value)}
              autoComplete="new-password"
              placeholder="Digite a senha novamente"
              minLength="6"
              maxLength="72"
              disabled={loading}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((visible) => !visible)}
              aria-label={showConfirmPassword ? 'Ocultar confirmação' : 'Mostrar confirmação'}
              title={showConfirmPassword ? 'Ocultar confirmação' : 'Mostrar confirmação'}
              disabled={loading}
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
            </button>
          </div>

          {error && <div className="quick-access-error" role="alert">{error}</div>}

          <button type="submit" className="quick-access-submit" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar meu progresso'}
          </button>
        </form>

        <p className="quick-access-existing">
          Já possui conta? Use o mesmo e-mail e a mesma senha para continuar.
        </p>
        <button
          type="button"
          className="quick-access-later"
          onClick={onDismiss}
          disabled={loading}
        >
          {requiresAccount ? 'Voltar para a aula' : 'Continuar por enquanto'}
        </button>
      </section>
    </div>
  );
};

export default QuickAccessModal;
